import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Drawer } from '../ui/drawer';
import { api, type KbCitation, type KbCitationSource } from '../../lib/api';
import { formatCitationSource, useSourceLabels } from '../../lib/source-label';
import { cn, formatDate } from '../../lib/utils';

/**
 * Raw source with the cited span highlighted.
 *
 * The highlight offsets come from the server, which computes them with the same normalization the
 * quote matching uses — re-deriving them in the browser is how a highlight ends up a few
 * characters off from the text it is supposed to mark.
 */
function SourceView({ data }: { data: KbCitationSource }) {
  const markRef = useRef<HTMLElement>(null);

  // A source document can be thousands of lines; landing on the cited span is the point of
  // opening this at all.
  useEffect(() => {
    markRef.current?.scrollIntoView({ block: 'center' });
  }, [data.citation.id]);

  if (!data.source) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-warning-text bg-warning-bg border border-warning-ring rounded px-3 py-2">
          The original source has been removed. The recorded quote is preserved below.
        </p>
        <blockquote className="text-xs text-ink-secondary border-l-2 border-orange-400 pl-3 py-1 italic">
          {data.citation.sourceQuote}
        </blockquote>
      </div>
    );
  }

  const { content } = data.source;
  const span = data.span;

  // No max-height in the drawer: the panel scrolls as a whole, so a nested scroll region would
  // trap the wheel and make a long source harder to read, not easier.
  return (
    <div className="rounded-md border border-border bg-surface-subtle">
      <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words p-3 font-mono text-ink-secondary">
        {span ? (
          <>
            {content.slice(0, span.start)}
            <mark ref={markRef} className="bg-orange-200 dark:bg-orange-900/70 text-ink rounded px-0.5">
              {content.slice(span.start, span.end)}
            </mark>
            {content.slice(span.end)}
          </>
        ) : (
          content
        )}
      </pre>
    </div>
  );
}

/**
 * The evidence behind one citation: the claim, the sentence it supports, and the raw source with
 * the quoted span highlighted. Opened both from the Sources panel and from an inline marker in the
 * prose, so it lives on its own rather than inside either.
 *
 * A right-side drawer rather than a modal: this is for reading a source document alongside the
 * page that cites it, and a centred modal covers the very prose you are checking against. The
 * drawer leaves the node visible on the left.
 */
export function CitationSourceDrawer({
  citation, projectId, onClose,
}: {
  citation: KbCitation | null;
  projectId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<KbCitationSource | null>(null);
  const sourceLabels = useSourceLabels();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!citation) return;
    setData(null);
    setError(null);
    setLoading(true);
    api.kb
      .citationSource(projectId, citation.id)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [citation, projectId]);

  return (
    <Drawer open={citation !== null} onClose={onClose} title="Citation source" className="max-w-xl">
      {citation && (
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-1">Claim</p>
            <p className="text-sm text-ink leading-snug">{citation.claim}</p>
          </div>

          <div>
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-1">
              Supports this text in the page
            </p>
            <blockquote
              className={cn(
                'text-xs border-l-2 pl-3 py-1',
                citation.current
                  ? 'border-green-400 text-ink-secondary'
                  : 'border-border-strong text-ink-faint line-through',
              )}
            >
              {citation.nodeQuote}
            </blockquote>
            {!citation.current && (
              <p className="text-[10px] text-warning-text mt-1.5 flex items-center gap-1">
                <AlertTriangle size={10} />
                This sentence is no longer in the page — a later ingest rewrote it.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">Raw source</p>
              {data?.source && (
                <span className="text-[10px] text-ink-faint">
                  {formatCitationSource(data.source, sourceLabels)} · {data.source.sourceType.toLowerCase()} ·{' '}
                  {formatDate(data.source.capturedAt)}
                </span>
              )}
            </div>

            {loading && (
              <p className="text-[11px] text-ink-faint flex items-center gap-1.5 py-3">
                <Loader2 size={11} className="animate-spin" />Loading source…
              </p>
            )}
            {error && (
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
                {error}
              </p>
            )}
            {data && <SourceView data={data} />}
            {data && !data.span && data.source && (
              <p className="text-[10px] text-ink-faint mt-1.5">
                Couldn't locate the quoted span in the source — showing the document in full.
              </p>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
